/* Wallet · creazione e rinomina.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DOVE VIENE CIFRATA LA CHIAVE PRIVATA, E PERCHÉ NON QUI
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Nel progetto convivono DUE tabelle diverse che contengono chiavi, e si
 * comportano in modo opposto. Confonderle costa il funzionamento di un bot che
 * muove denaro vero, quindi la distinzione sta scritta qui e non altrove:
 *
 * 1. `personal_wallet` (`GET /personal-wallets`) — colonne `privateKey`,
 *    `mnemonic`, `address`. L'entity NON ha alcun hook di cifratura: il server
 *    scrive a database esattamente la stringa che riceve. È per questo che il
 *    frontend precedente, in `PersonalWalletsContainer.js`, cifra nel browser
 *    con `crypto-js` e una password (i valori a database iniziano infatti con
 *    `U2FsdGVkX1…`, il preambolo di crypto-js). Quella è una cassaforte
 *    personale: nessun bot la legge, nessuno deve poterla decifrare da solo.
 *
 * 2. `wallet` (`GET /wallets`) — è QUESTA, quella dei tradebot on-chain.
 *    L'entity `Wallet` ha `@BeforeInsert() encryptPrivateKey()` che chiama
 *    `encrypt()` di `src/utils/wallet.util.ts`: AES-256-CBC con la SECURITY_KEY
 *    del server, risultato in esadecimale. Il campo si chiama
 *    `encryptedPrivateKey` ma in ingresso vuole la chiave IN CHIARO: è il
 *    server a cifrarla, un istante prima di scriverla.
 *
 * Cifrare qui nel browser, prima dell'invio, romperebbe due cose in modo
 * silenzioso e irreversibile:
 *
 *  - `WalletTraderService` (riga 74) fa `decrypt(wallet.encryptedPrivateKey)`
 *    per firmare le transazioni. Con una doppia cifratura ne otterrebbe il
 *    testo cifrato di crypto-js, non una chiave: il bot non firma più nulla,
 *    e la password che servirebbe a chiudere il cerchio non ce l'ha nessuno
 *    (per costruzione: non viene salvata da nessuna parte);
 *  - `deriveAddress()` gira sulla chiave in chiaro PRIMA della cifratura per
 *    ricavare l'indirizzo pubblico. Su un testo cifrato fallisce e restituisce
 *    `null`: il wallet resta senza indirizzo, e senza indirizzo non si leggono
 *    i saldi — cioè l'intera schermata Wallet.
 *
 * Il frontend precedente lo sapeva: `WalletFormContainer.js` manda
 * `encryptedPrivateKey` così com'è digitato, senza toccare `crypto-js`. La
 * cifratura nel browser sta solo in `PersonalWalletsContainer.js`, sull'altra
 * tabella. Qui si fa la stessa cosa, per la stessa ragione.
 *
 * Quello che invece resta identico al comportamento richiesto, e vale su
 * entrambe le tabelle:
 *  - la chiave attraversa il browser UNA volta sola, nel corpo del POST;
 *  - non finisce in `localStorage`, in una query string, in un log o in un
 *    messaggio d'errore;
 *  - il campo è `type="password"` con `autoComplete="off"` e si svuota sia
 *    all'invio sia alla chiusura del modulo;
 *  - la risposta del server contiene `encryptedPrivateKey` (il testo cifrato):
 *    viene scartata subito con `soloDatiPubblici`, così il testo cifrato non
 *    entra nemmeno nello stato dell'applicazione.
 */

import { useEffect, useState } from 'react';
import { Avvertenza, Pulsante } from '../../componenti';
import { Campo } from '../../componenti/Modulo';
import { Modale } from '../../componenti/Sovrapposizioni';
import { wallets } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import { soloDatiPubblici, type WalletPubblico } from './dati';

/* ==========================================================================
 * Forma della chiave: l'unico controllo che si può fare senza libreria
 * ======================================================================== */

/**
 * Il server accetta qualunque stringa e la cifra: una chiave sbagliata NON dà
 * errore, dà un wallet salvato con `address: null` — cioè un wallet che non
 * mostrerà mai un saldo e su cui nessun bot potrà operare. Il controllo di
 * forma serve a intercettarlo prima di scriverlo, non a validare la chiave.
 *
 * Le due forme che `ethers` accetta lato server sono:
 *  - chiave privata: 32 byte in esadecimale, con o senza `0x`;
 *  - frase mnemonica BIP-39: 12, 15, 18, 21 o 24 parole.
 *
 * La correttezza vera (checksum BIP-39, curva della chiave) non è verificabile
 * qui senza aggiungere `ethers` al browser solo per questo: la verifica arriva
 * comunque un istante dopo, ed è l'indirizzo derivato dal server.
 */
const PAROLE_MNEMONICA = [12, 15, 18, 21, 24];

function formaChiave(valore: string): 'chiave' | 'mnemonica' | null {
  const v = valore.trim();
  if (/^(0x)?[0-9a-fA-F]{64}$/.test(v)) return 'chiave';
  const parole = v.split(/\s+/).filter(Boolean);
  if (PAROLE_MNEMONICA.includes(parole.length) && parole.every((p) => /^[a-z]+$/.test(p))) {
    return 'mnemonica';
  }
  return null;
}

/**
 * Normalizza prima dell'invio. `ethers.Wallet` lato server pretende il prefisso
 * `0x` sulla chiave esadecimale: senza, `deriveAddress` fallisce in silenzio e
 * il wallet nasce senza indirizzo. La mnemonica viene solo ricompattata negli
 * spazi, perché incollandola da un foglio arrivano a capo e doppi spazi.
 */
function normalizzaChiave(valore: string): string {
  const v = valore.trim();
  if (/^[0-9a-fA-F]{64}$/.test(v)) return `0x${v}`;
  if (/^0x[0-9a-fA-F]{64}$/.test(v)) return v;
  return v.split(/\s+/).filter(Boolean).join(' ');
}

/* ==========================================================================
 * Creazione
 * ======================================================================== */

export function ModuloNuovoWallet({
  aperto,
  onChiudi,
  nomiEsistenti,
  onCreato,
}: {
  aperto: boolean;
  onChiudi: () => void;
  /** Serve a fermare il nome duplicato prima del 500: `wallet.name` è UNIQUE. */
  nomiEsistenti: string[];
  onCreato: (wallet: WalletPubblico) => void;
}) {
  const [nome, setNome] = useState('');
  const [chiave, setChiave] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /** Svuota il segreto. Si chiama all'invio e alla chiusura: una chiave che
   *  resta nello stato di React è una chiave che ricompare riaprendo il
   *  modulo, e intanto è rimasta in memoria per tutta la sessione. */
  function svuota() {
    setChiave('');
  }

  const nomePulito = nome.trim();
  const nomeDuplicato =
    nomePulito !== '' &&
    nomiEsistenti.some((n) => n.trim().toLowerCase() === nomePulito.toLowerCase());

  const forma = chiave.trim() === '' ? null : formaChiave(chiave);
  const chiaveIrriconoscibile = chiave.trim() !== '' && forma === null;

  const valido = nomePulito !== '' && !nomeDuplicato && forma !== null;

  async function invia() {
    if (!valido || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      const creato = await wallets.crea({
        name: nomePulito,
        encryptedPrivateKey: normalizzaChiave(chiave),
      });
      /* La risposta riporta `encryptedPrivateKey` cifrato: si proietta subito
         sui soli campi pubblici, così il testo cifrato non entra nello stato
         dell'applicazione né in nessun componente. */
      const pubblico = soloDatiPubblici(creato);
      svuota();
      setNome('');
      onCreato(pubblico);
    } catch (e) {
      /* Il messaggio del server non contiene la chiave, e non gliela si
         aggiunge: niente `console.error` con il corpo della richiesta dentro. */
      setErrore(
        e instanceof ErroreApi
          ? e.stato === 409 || e.stato === 500
            ? 'Creazione non riuscita: il nome potrebbe essere già in uso.'
            : e.message
          : 'Creazione non riuscita.',
      );
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modale
      aperta={aperto}
      onChiudi={() => {
        svuota();
        setErrore(null);
        onChiudi();
      }}
      titolo="Aggiungi un wallet"
      sottotitolo="la chiave parte una sola volta, in questa richiesta"
      pieda={
        <Pulsante variante="primario" largo onClick={invia} disabled={!valido || inCorso}>
          {inCorso ? 'Creazione in corso…' : 'Crea il wallet'}
        </Pulsante>
      }
    >
      <div className="sb-colonna">
        <Avvertenza livello="grave">
          Chi ha questa chiave ha i fondi. Da qui viaggia una volta sola, nel corpo di questa
          richiesta: il server la cifra prima di scriverla a database, in chiaro non viene mai
          salvata. Non torna più indietro e SemBot non te la rimostrerà: tieni la tua copia fuori
          da qui, perché è l'unica che puoi rileggere.
        </Avvertenza>

        <Campo
          etichetta="Nome"
          testuale
          suggerimento="deve essere unico"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          autoComplete="off"
          errore={nomeDuplicato ? 'Esiste già un wallet con questo nome.' : null}
        />

        {/* type=password anche se non è una password: su un telefono in treno
            lo schermo è pubblico, e questa riga vale quanto il wallet. */}
        <Campo
          etichetta="Chiave privata o frase mnemonica"
          type="password"
          testuale
          suggerimento="64 caratteri esadecimali, oppure 12/24 parole"
          value={chiave}
          onChange={(e) => setChiave(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          errore={
            chiaveIrriconoscibile
              ? 'Non è né una chiave da 64 caratteri esadecimali né una frase da 12, 15, 18, 21 o 24 parole.'
              : null
          }
        />

        {forma === 'mnemonica' && (
          <Avvertenza livello="nota">
            Riconosciuta come frase mnemonica: il server ne ricava la chiave privata del primo
            indirizzo. Le parole non vengono controllate una per una — se una è sbagliata, il
            wallet nascerà senza indirizzo e te lo dirà l'elenco.
          </Avvertenza>
        )}

        <Avvertenza livello="cautela">
          La cifratura a database usa la chiave di sistema del server, non una password tua: non c'è
          niente da ricordare, ma non c'è nemmeno niente da recuperare per conto tuo. Se il server
          perde la sua chiave di sistema, i wallet salvati qui non sono più decifrabili da nessuno.
        </Avvertenza>

        {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}
      </div>
    </Modale>
  );
}

/* ==========================================================================
 * Rinomina
 * ======================================================================== */

/**
 * Si cambia solo il nome, ed è una scelta obbligata dal backend: la cifratura
 * sta in `@BeforeInsert`, che gira sull'INSERT e non sull'UPDATE. Una PATCH con
 * `encryptedPrivateKey` scriverebbe la chiave IN CHIARO a database, e il bot
 * poi la passerebbe a `decrypt()` ottenendo spazzatura. Finché non esiste un
 * endpoint di rotazione (richiesta W9 in RICHIESTE-API.md), da qui la chiave
 * non si tocca — e il modulo lo dice, invece di mostrare un campo inerte.
 */
export function ModuloRinominaWallet({
  wallet,
  onChiudi,
  nomiEsistenti,
  onRinominato,
}: {
  wallet: WalletPubblico | null;
  onChiudi: () => void;
  nomiEsistenti: string[];
  onRinominato: (nome: string) => void;
}) {
  const [nome, setNome] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /* Il nome corrente si carica all'apertura: un modulo di modifica che parte
     vuoto fa credere che il valore vecchio sia stato perso. */
  useEffect(() => {
    if (wallet) {
      setNome(wallet.nome);
      setErrore(null);
    }
  }, [wallet]);

  const nomePulito = nome.trim();
  const nomeDuplicato =
    nomePulito !== '' &&
    nomiEsistenti.some(
      (n) =>
        n.trim().toLowerCase() === nomePulito.toLowerCase() &&
        n.trim().toLowerCase() !== (wallet?.nome ?? '').trim().toLowerCase(),
    );
  const cambiato = wallet != null && nomePulito !== wallet.nome.trim();
  const valido = nomePulito !== '' && !nomeDuplicato && cambiato;

  async function invia() {
    if (!wallet || !valido || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      /* Solo `name`: vedi il commento sopra sul perché la chiave non può
         passare da una PATCH. */
      await wallets.aggiorna(wallet.id, { name: nomePulito });
      onRinominato(nomePulito);
    } catch (e) {
      setErrore(
        e instanceof ErroreApi
          ? e.stato === 409 || e.stato === 500
            ? 'Rinomina non riuscita: il nome potrebbe essere già in uso.'
            : e.message
          : 'Rinomina non riuscita.',
      );
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modale
      aperta={wallet != null}
      onChiudi={() => {
        setErrore(null);
        onChiudi();
      }}
      titolo="Rinomina il wallet"
      sottotitolo={wallet?.nome}
      pieda={
        <Pulsante variante="primario" largo onClick={invia} disabled={!valido || inCorso}>
          {inCorso ? 'Salvataggio in corso…' : 'Salva il nome'}
        </Pulsante>
      }
    >
      <div className="sb-colonna">
        <Campo
          etichetta="Nome"
          testuale
          suggerimento="deve essere unico"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          autoComplete="off"
          errore={nomeDuplicato ? 'Esiste già un altro wallet con questo nome.' : null}
        />

        <Avvertenza livello="nota">
          Da qui si cambia solo il nome. La chiave privata non si sostituisce: il backend la cifra
          soltanto al momento della creazione, quindi una modifica la scriverebbe in chiaro e il bot
          non riuscirebbe più a firmare. Per cambiare chiave si crea un wallet nuovo, si spostano i
          fondi e si sposta il bot.
        </Avvertenza>

        {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}
      </div>
    </Modale>
  );
}
